import { useState, useRef, useEffect } from "react";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type DealerPortalCustomerJourneyStatus,
  type DealerPortalJourneyPriority,
} from "@/types/dealerPortal";
import type {
  DealerPortalCustomer,
  DealerPortalVehicle,
} from "@/types/dealerPortal";
import { DealerPortalCreateCustomerDialog } from "./DealerPortalCreateCustomerDialog";
import { DealerPortalCreateVehicleDialog } from "./DealerPortalCreateVehicleDialog";
import {
  Search,
  UserPlus,
  Car,
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";
import { useLanguage } from "@/context/LanguageContext";
import {
  dealerPortalLabels,
  getJourneyStatusLabels,
  getJourneyPriorityLabels,
} from "@/i18n";

const PAGE_SIZE = 10;

const STATUS_ORDER: DealerPortalCustomerJourneyStatus[] = [
  "FIRST_CONTACT",
  "PRODUCT_VIEWING",
  "NEGOTIATION",
  "CLOSED",
  "NOT_COMPLETED",
];

const STATUS_COLORS: Record<DealerPortalCustomerJourneyStatus, string> = {
  FIRST_CONTACT: "from-blue-500 to-purple-600",
  PRODUCT_VIEWING: "from-purple-500 to-purple-700",
  NEGOTIATION: "from-pink-500 to-purple-600",
  CLOSED: "from-emerald-500 to-green-600",
  NOT_COMPLETED: "from-orange-500 to-red-600",
};

const STATUS_ICONS: Record<DealerPortalCustomerJourneyStatus, LucideIcon> = {
  FIRST_CONTACT: Users,
  PRODUCT_VIEWING: Calendar,
  NEGOTIATION: TrendingUp,
  CLOSED: CheckCircle,
  NOT_COMPLETED: AlertCircle,
};

const PRIORITY_BADGE: Record<string, string> = {
  HIGH: "bg-red-500/90 text-white",
  MEDIUM: "bg-amber-500/90 text-white",
  LOW: "bg-emerald-500/90 text-white",
};
const PRIORITY_BADGE_HIGHLIGHT: Record<string, string> = {
  HIGH: "data-[highlighted]:!bg-red-500/90",
  MEDIUM: "data-[highlighted]:!bg-amber-500/90",
  LOW: "data-[highlighted]:!bg-emerald-500/90",
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function DealerPortalNewFollowUpModal({
  open,
  onClose,
  onSuccess,
}: Props) {
  const { language } = useLanguage();
  const statusLabels = getJourneyStatusLabels(language);
  const priorityLabels = getJourneyPriorityLabels(language);
  const nf = dealerPortalLabels(language).newFollowUp as Record<string, string>;
  const [customerId, setCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<DealerPortalCustomer | null>(null);
  const [vehicleId, setVehicleId] = useState<string>("__none__");
  const [selectedVehicle, setSelectedVehicle] =
    useState<DealerPortalVehicle | null>(null);
  const [status, setStatus] =
    useState<DealerPortalCustomerJourneyStatus>("FIRST_CONTACT");
  const [priority, setPriority] =
    useState<DealerPortalJourneyPriority>("MEDIUM");
  const [motivation, setMotivation] = useState("");
  const nativeSheetProps = useDealerPortalNativeSheetProps(
    open,
    "max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] p-0 flex flex-col overflow-hidden",
  );

  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPage, setCustomerPage] = useState(1);
  const [customerItems, setCustomerItems] = useState<DealerPortalCustomer[]>(
    [],
  );
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerLoading, setCustomerLoading] = useState(false);
  const customerDropdownRef = useRef<HTMLDivElement>(null);

  const [vehicleDropdownOpen, setVehicleDropdownOpen] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehicleItems, setVehicleItems] = useState<DealerPortalVehicle[]>([]);
  const [vehicleTotal, setVehicleTotal] = useState(0);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const vehicleDropdownRef = useRef<HTMLDivElement>(null);

  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);

  const {
    createJourney: createMutation,
    getCustomersPaginated,
    getVehiclesPaginated,
  } = useDealerPortal();

  // Nollställ formulär när modalen öppnas
  useEffect(() => {
    if (open) {
      setCustomerId("");
      setSelectedCustomer(null);
      setVehicleId("__none__");
      setSelectedVehicle(null);
      setStatus("FIRST_CONTACT");
      setPriority("MEDIUM");
      setMotivation("");
      setCustomerDropdownOpen(false);
      setCustomerSearch("");
      setCustomerPage(1);
      setCustomerItems([]);
      setCustomerTotal(0);
      setVehicleDropdownOpen(false);
      setVehicleSearch("");
      setVehiclePage(1);
      setVehicleItems([]);
      setVehicleTotal(0);
      setCreateCustomerOpen(false);
      setCreateVehicleOpen(false);
    }
  }, [open]);

  const fetchCustomers = async (page: number, search: string) => {
    setCustomerLoading(true);
    try {
      const res = await getCustomersPaginated({
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setCustomerItems(res.items);
      setCustomerTotal(res.total);
      setCustomerPage(page);
    } catch {
      setCustomerItems([]);
      setCustomerTotal(0);
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchVehicles = async (page: number, search: string) => {
    setVehicleLoading(true);
    try {
      const res = await getVehiclesPaginated({
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
      });
      setVehicleItems(res.items);
      setVehicleTotal(res.total);
      setVehiclePage(page);
    } catch {
      setVehicleItems([]);
      setVehicleTotal(0);
    } finally {
      setVehicleLoading(false);
    }
  };

  useEffect(() => {
    if (customerDropdownOpen) fetchCustomers(1, customerSearch);
  }, [customerDropdownOpen]);

  useEffect(() => {
    if (vehicleDropdownOpen) fetchVehicles(1, vehicleSearch);
  }, [vehicleDropdownOpen]);

  const handleCustomerSearch = () => fetchCustomers(1, customerSearch);
  const handleVehicleSearch = () => fetchVehicles(1, vehicleSearch);

  const customerTotalPages = Math.max(1, Math.ceil(customerTotal / PAGE_SIZE));
  const vehicleTotalPages = Math.max(1, Math.ceil(vehicleTotal / PAGE_SIZE));

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(e.target as Node)
      )
        setCustomerDropdownOpen(false);
      if (
        vehicleDropdownRef.current &&
        !vehicleDropdownRef.current.contains(e.target as Node)
      )
        setVehicleDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) return;
    createMutation.mutate(
      {
        customerId,
        vehicleId: vehicleId && vehicleId !== "__none__" ? vehicleId : null,
        status,
        priority,
        followUpNote: motivation || undefined,
      },
      {
        onSuccess: () => onSuccess(),
      },
    );
  };

  const customerLabel = selectedCustomer
    ? selectedCustomer.name ||
      selectedCustomer.email ||
      selectedCustomer.phone ||
      selectedCustomer.id
    : "";
  const vehicleLabel = selectedVehicle
    ? `${selectedVehicle.regNr} – ${selectedVehicle.make} ${selectedVehicle.model}`
    : "";

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent {...nativeSheetProps}>
        <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 flex-shrink-0 border-b">
          <DialogTitle className="text-sm sm:text-base">
            {nf.title ?? "Ny uppföljning"}
          </DialogTitle>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {nf.desc ?? "Skapa en ny uppföljning för kunden"}
          </p>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 flex flex-col overflow-hidden"
        >
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-4 sm:pb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-3">
              <div className="space-y-2 min-w-0" ref={customerDropdownRef}>
                <div className="flex items-center justify-between gap-2">
                  <Label>{nf.customer ?? "Kund"}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground h-8 shrink-0"
                    onClick={() => setCreateCustomerOpen(true)}
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    {nf.createCustomer ?? "Skapa kund"}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder={
                      nf.searchOrSelectCustomer ?? "Sök eller välj kund"
                    }
                    value={
                      customerDropdownOpen ? customerSearch : customerLabel
                    }
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onFocus={() => setCustomerDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCustomerSearch();
                      }
                    }}
                    className="min-w-0"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCustomerSearch}
                    disabled={customerLoading}
                    className="shrink-0"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                {customerDropdownOpen && (
                  <div className="border rounded-md bg-popover shadow-md max-h-64 flex flex-col">
                    <div className="p-1 overflow-auto flex-1">
                      {customerLoading ? (
                        <p className="p-2 text-sm text-muted-foreground">
                          {nf.loading ?? "Laddar..."}
                        </p>
                      ) : customerItems.length === 0 ? (
                        <p className="p-2 text-sm text-muted-foreground">
                          {nf.noResults ?? "Inga träffar."}
                        </p>
                      ) : (
                        customerItems.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent/50"
                            onClick={() => {
                              setSelectedCustomer(c);
                              setCustomerId(c.id);
                              setCustomerDropdownOpen(false);
                            }}
                          >
                            {c.name || c.email || c.phone || c.id}
                          </button>
                        ))
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 p-2 border-t text-xs text-muted-foreground">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={customerPage <= 1 || customerLoading}
                        onClick={() =>
                          fetchCustomers(customerPage - 1, customerSearch)
                        }
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span>
                        {(nf.pageOf ?? "Sida {page} av {total}")
                          .replace("{page}", String(customerPage))
                          .replace("{total}", String(customerTotalPages))}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={
                          customerPage >= customerTotalPages || customerLoading
                        }
                        onClick={() =>
                          fetchCustomers(customerPage + 1, customerSearch)
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 min-w-0" ref={vehicleDropdownRef}>
                <div className="flex items-center justify-between gap-2">
                  <Label>{nf.vehicleOptional ?? "Fordon (valfritt)"}</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground h-8 shrink-0"
                    onClick={() => setCreateVehicleOpen(true)}
                  >
                    <Car className="h-4 w-4 mr-1" />
                    {nf.createVehicle ?? "Skapa fordon"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {nf.vehicleLinkHint ??
                    "Kunden behöver inte vara ägare till fordonet – kopplingen visar t.ex. intresse eller pågående uppföljning."}
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder={
                      nf.searchOrSelectVehicle ?? "Sök eller välj fordon"
                    }
                    value={
                      vehicleDropdownOpen
                        ? vehicleSearch
                        : vehicleLabel || (nf.noVehicle ?? "Inget fordon")
                    }
                    onChange={(e) => setVehicleSearch(e.target.value)}
                    onFocus={() => setVehicleDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleVehicleSearch();
                      }
                    }}
                    className="min-w-0"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleVehicleSearch}
                    disabled={vehicleLoading}
                    className="shrink-0"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                {vehicleDropdownOpen && (
                  <div className="border rounded-md bg-popover shadow-md max-h-64 flex flex-col">
                    <div className="p-1 overflow-auto flex-1">
                      {vehicleLoading ? (
                        <p className="p-2 text-sm text-muted-foreground">
                          {nf.loading ?? "Laddar..."}
                        </p>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent/50"
                            onClick={() => {
                              setSelectedVehicle(null);
                              setVehicleId("__none__");
                              setVehicleDropdownOpen(false);
                            }}
                          >
                            {nf.noVehicle ?? "Inget fordon"}
                          </button>
                          {vehicleItems.length === 0 && (
                            <p className="p-2 text-sm text-muted-foreground">
                              {nf.noResultsVehicle ?? "Inga träffar."}
                            </p>
                          )}
                          {vehicleItems.map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent/50"
                              onClick={() => {
                                setSelectedVehicle(v);
                                setVehicleId(v.id);
                                setVehicleDropdownOpen(false);
                              }}
                            >
                              {v.regNr} – {v.make} {v.model} ({v.year})
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2 p-2 border-t text-xs text-muted-foreground">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={vehiclePage <= 1 || vehicleLoading}
                        onClick={() =>
                          fetchVehicles(vehiclePage - 1, vehicleSearch)
                        }
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span>
                        {(nf.pageOf ?? "Sida {page} av {total}")
                          .replace("{page}", String(vehiclePage))
                          .replace("{total}", String(vehicleTotalPages))}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={
                          vehiclePage >= vehicleTotalPages || vehicleLoading
                        }
                        onClick={() =>
                          fetchVehicles(vehiclePage + 1, vehicleSearch)
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 min-w-0">
                <Label>{nf.journeyStep ?? "Steg i kundresan"}</Label>
                <Select
                  value={status}
                  onValueChange={(v) =>
                    setStatus(v as DealerPortalCustomerJourneyStatus)
                  }
                >
                  <SelectTrigger
                    className={
                      status
                        ? `bg-gradient-to-r ${STATUS_COLORS[status]} text-white border-0 [&>svg]:text-white`
                        : ""
                    }
                  >
                    <SelectValue>
                      {status ? (
                        <span className="flex items-center gap-2">
                          {(() => {
                            const Icon = STATUS_ICONS[status];
                            return (
                              <Icon className="h-4 w-4 shrink-0 opacity-90" />
                            );
                          })()}
                          {statusLabels[status]}
                        </span>
                      ) : null}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_ORDER.map((s) => {
                      const Icon = STATUS_ICONS[s];
                      return (
                        <SelectItem
                          key={s}
                          value={s}
                          className={`bg-gradient-to-r ${STATUS_COLORS[s]} text-white border-0 group focus:text-white data-[highlighted]:text-white`}
                        >
                          <span className="inline-flex items-center gap-2 transition-transform duration-200 group-data-[highlighted]:scale-[1.05]">
                            <Icon className="h-4 w-4 shrink-0 opacity-90" />
                            {statusLabels[s]}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 min-w-0">
                <Label>
                  {(
                    dealerPortalLabels(language).journeyModal as Record<
                      string,
                      string
                    >
                  ).priority ?? "Prioritet"}
                </Label>
                <Select
                  value={priority}
                  onValueChange={(v) =>
                    setPriority(v as DealerPortalJourneyPriority)
                  }
                >
                  <SelectTrigger
                    className={`rounded-full ${PRIORITY_BADGE[priority] ?? PRIORITY_BADGE.MEDIUM} border-0 text-white [&>svg]:text-white`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      Object.keys(
                        priorityLabels,
                      ) as DealerPortalJourneyPriority[]
                    ).map((p) => (
                      <SelectItem
                        key={p}
                        value={p}
                        className={`${PRIORITY_BADGE[p] ?? PRIORITY_BADGE.MEDIUM} ${PRIORITY_BADGE_HIGHLIGHT[p] ?? PRIORITY_BADGE_HIGHLIGHT.MEDIUM} text-white rounded-sm border-0 group focus:text-white data-[highlighted]:text-white`}
                      >
                        <span className="inline-block transition-transform duration-200 group-data-[highlighted]:scale-[1.05]">
                          {priorityLabels[p]}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{nf.commentOptional ?? "Kommentar (valfritt)"}</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm"
                placeholder={
                  nf.commentPlaceholder ??
                  "Lägg till en kommentar till uppföljningen"
                }
                value={motivation}
                onChange={(e) => setMotivation(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {(
                  dealerPortalLabels(language).customers as Record<
                    string,
                    string
                  >
                ).cancel ?? "Avbryt"}
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending
                  ? ((
                      dealerPortalLabels(language).customers as Record<
                        string,
                        string
                      >
                    ).saving ?? "Sparar...")
                  : (nf.saveFollowUp ?? "Spara uppföljning")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>

      <DealerPortalCreateCustomerDialog
        open={createCustomerOpen}
        nested={open}
        onClose={() => setCreateCustomerOpen(false)}
        onSuccess={(customer) => {
          setSelectedCustomer(customer);
          setCustomerId(customer.id);
          setCustomerDropdownOpen(false);
        }}
      />
      <DealerPortalCreateVehicleDialog
        open={createVehicleOpen}
        nested={open}
        onClose={() => setCreateVehicleOpen(false)}
        onSuccess={(vehicle) => {
          setSelectedVehicle(vehicle);
          setVehicleId(vehicle.id);
          setVehicleDropdownOpen(false);
        }}
      />
    </>
  );
}
